/**
 * Client-only marketing routes for prerendered marketing pages. Pages are
 * lazy-loaded so the public client entry does not eagerly pull every SSG page
 * into the first chunk.
 *
 * Keep this file's route list in sync with `marketing-routes.ssr.tsx` and
 * `seo-metadata.ts`.
 */
import { lazy } from 'react';
import { Route } from 'react-router-dom';
import { MarketingRouteBoundary } from './marketing-route-boundary';
import { SECURITY_PUBLICATION_ENABLED } from './security-launch-control.vite';

const LandingPage = lazy(() => import('./pages/LandingPage'));
const HowItWorksPage = lazy(() => import('./pages/HowItWorksPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const AffiliateProgramPage = lazy(() => import('./pages/AffiliateProgramPage'));
const RoadmapPage = lazy(() => import('./pages/RoadmapPage'));
const ReleaseNotesPage = lazy(() => import('./pages/ReleaseNotesPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const AppCheckPage = lazy(() => import('./pages/AppCheckPage'));
const MethodologyPage = lazy(() => import('./pages/MethodologyPage'));
const VsApptweakPage = lazy(() => import('./pages/VsApptweakPage'));
const ForAgenciesPage = lazy(() => import('./pages/ForAgenciesPage'));
const ForGrowthTeamsPage = lazy(() => import('./pages/ForGrowthTeamsPage'));
const ForPublishersPage = lazy(() => import('./pages/ForPublishersPage'));
const ForConsultantsPage = lazy(() => import('./pages/ForConsultantsPage'));
const DiyLlmVsApptonomyPage = lazy(() => import('./pages/DiyLlmVsApptonomyPage'));
const RefundPolicyPage = lazy(() => import('./pages/RefundPolicyPage'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage'));
const EngineAIDiscoverabilityPage = lazy(() => import('./pages/EngineAIDiscoverabilityPage'));
const EngineKeywordsPage = lazy(() => import('./pages/EngineKeywordsPage'));
const EngineUserIntentPage = lazy(() => import('./pages/EngineUserIntentPage'));
const EngineStoreTextPage = lazy(() => import('./pages/EngineStoreTextPage'));
const EngineScreenshotsPage = lazy(() => import('./pages/EngineScreenshotsPage'));
const EngineIconPage = lazy(() => import('./pages/EngineIconPage'));
const EngineSentimentPage = lazy(() => import('./pages/EngineSentimentPage'));
const EngineCompetitorsPage = lazy(() => import('./pages/EngineCompetitorsPage'));
const EngineLocalizationPage = lazy(() => import('./pages/EngineLocalizationPage'));
const EnginePolicyCheckerPage = lazy(() => import('./pages/EnginePolicyCheckerPage'));
const PublishingPage = lazy(() => import('./pages/PublishingPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
// A7Y-5452: the marketing entry carries /start's stylesheet in its entry sheet
// (src/marketing/index.css) so the prerendered page paints before any script.
// The app shell, which serves /start in local dev and on the sandbox, has no
// such sheet, so there the page brings its own. The marketing build folds the
// condition to its first branch and never includes the second import.
const StartPage = lazy(() =>
  import.meta.env.VITE_A7Y_MARKETING_ENTRY === 'true'
    ? import('./pages/StartPage')
    : Promise.all([import('./pages/StartPage'), import('./start/start-styles.css')]).then(([page]) => page)
);
const AsoCalculatorPage = lazy(() => import('./pages/AsoCalculatorPage'));
const SecurityPage = lazy(() => import('./pages/SecurityPage'));
const StoreCredentialSecurityPage = lazy(() => import('./pages/security/StoreCredentialSecurityPage'));
const RiskGuardPage = lazy(() => import('./pages/security/RiskGuardPage'));
const Soc2Page = lazy(() => import('./pages/security/Soc2Page'));
const Iso27001Page = lazy(() => import('./pages/security/Iso27001Page'));
const DpaPage = lazy(() => import('./pages/DpaPage'));
const SubprocessorsPage = lazy(() => import('./pages/SubprocessorsPage'));
const SecurityAdvisoriesPage = lazy(() => import('./pages/SecurityAdvisoriesPage'));

export function MarketingRoutes() {
  return (
    <>
      <Route
        path="/"
        element={
          <MarketingRouteBoundary>
            <LandingPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/pricing"
        element={
          <MarketingRouteBoundary>
            <PricingPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/how-it-works"
        element={
          <MarketingRouteBoundary>
            <HowItWorksPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/methodology"
        element={
          <MarketingRouteBoundary>
            <MethodologyPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/about"
        element={
          <MarketingRouteBoundary>
            <AboutPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/affiliates"
        element={
          <MarketingRouteBoundary>
            <AffiliateProgramPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/roadmap"
        element={
          <MarketingRouteBoundary>
            <RoadmapPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/release-notes"
        element={
          <MarketingRouteBoundary>
            <ReleaseNotesPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/contact"
        element={
          <MarketingRouteBoundary>
            <ContactPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/refund-policy"
        element={
          <MarketingRouteBoundary>
            <RefundPolicyPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/terms"
        element={
          <MarketingRouteBoundary>
            <TermsOfServicePage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/privacy"
        element={
          <MarketingRouteBoundary>
            <PrivacyPolicyPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/cookie-policy"
        element={
          <MarketingRouteBoundary>
            <CookiePolicyPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/appcheck"
        element={
          <MarketingRouteBoundary>
            <AppCheckPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/vs/apptweak"
        element={
          <MarketingRouteBoundary>
            <VsApptweakPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/for/agencies"
        element={
          <MarketingRouteBoundary>
            <ForAgenciesPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/for/growth-teams"
        element={
          <MarketingRouteBoundary>
            <ForGrowthTeamsPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/for/publishers"
        element={
          <MarketingRouteBoundary>
            <ForPublishersPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/for/consultants"
        element={
          <MarketingRouteBoundary>
            <ForConsultantsPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/diy-llm-vs-apptonomy"
        element={
          <MarketingRouteBoundary>
            <DiyLlmVsApptonomyPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/ai-discoverability"
        element={
          <MarketingRouteBoundary>
            <EngineAIDiscoverabilityPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/keywords-and-semantic-clusters"
        element={
          <MarketingRouteBoundary>
            <EngineKeywordsPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/user-intent"
        element={
          <MarketingRouteBoundary>
            <EngineUserIntentPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/store-text"
        element={
          <MarketingRouteBoundary>
            <EngineStoreTextPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/screenshots"
        element={
          <MarketingRouteBoundary>
            <EngineScreenshotsPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/icon"
        element={
          <MarketingRouteBoundary>
            <EngineIconPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/sentiment"
        element={
          <MarketingRouteBoundary>
            <EngineSentimentPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/competitors"
        element={
          <MarketingRouteBoundary>
            <EngineCompetitorsPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/localization-analysis"
        element={
          <MarketingRouteBoundary>
            <EngineLocalizationPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/engines/policy-checker"
        element={
          <MarketingRouteBoundary>
            <EnginePolicyCheckerPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/features/publishing"
        element={
          <MarketingRouteBoundary>
            <PublishingPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/features/analytics"
        element={
          <MarketingRouteBoundary>
            <AnalyticsPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/start"
        element={
          <MarketingRouteBoundary>
            <StartPage />
          </MarketingRouteBoundary>
        }
      />
      <Route
        path="/tools/calc"
        element={
          <MarketingRouteBoundary>
            <AsoCalculatorPage />
          </MarketingRouteBoundary>
        }
      />
      {SECURITY_PUBLICATION_ENABLED ? (
        <>
          <Route path="/security" element={<MarketingRouteBoundary><SecurityPage /></MarketingRouteBoundary>} />
          <Route path="/security/store-credentials" element={<MarketingRouteBoundary><StoreCredentialSecurityPage /></MarketingRouteBoundary>} />
          <Route path="/security/riskguard" element={<MarketingRouteBoundary><RiskGuardPage /></MarketingRouteBoundary>} />
          <Route path="/security/soc2" element={<MarketingRouteBoundary><Soc2Page /></MarketingRouteBoundary>} />
          <Route path="/security/iso27001" element={<MarketingRouteBoundary><Iso27001Page /></MarketingRouteBoundary>} />
          <Route path="/dpa" element={<MarketingRouteBoundary><DpaPage /></MarketingRouteBoundary>} />
          <Route path="/subprocessors" element={<MarketingRouteBoundary><SubprocessorsPage /></MarketingRouteBoundary>} />
          <Route path="/security/advisories" element={<MarketingRouteBoundary><SecurityAdvisoriesPage /></MarketingRouteBoundary>} />
        </>
      ) : null}
    </>
  );
}
